// Universal file parser. Accepts CSV, XLSX/XLS, TXT, PDF.
// Returns a normalized ParsedFile object used across the app.
import Papa from "papaparse";
import * as XLSX from "xlsx";

export type ParsedFile = {
  kind: "table" | "text";
  fileName: string;
  mimeType: string;
  columns?: string[];
  rows?: Record<string, any>[];
  text?: string;
};

export const ACCEPTED_FILE_TYPES =
  ".csv,.xlsx,.xls,.txt,.pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/pdf";

export const ACCEPTED_HELP_TEXT = "Accepted formats: CSV, Excel, TXT, PDF";

export const UNSUPPORTED_MESSAGE =
  "File type not supported. Please upload CSV, Excel, TXT, or PDF";

function extOf(name: string) {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

async function parseCsv(file: File): Promise<ParsedFile> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (res) => {
        const rows = (res.data as Record<string, any>[]).filter(
          (r) => r && Object.keys(r).length,
        );
        const columns = res.meta.fields ?? (rows[0] ? Object.keys(rows[0]) : []);
        resolve({
          kind: "table",
          fileName: file.name,
          mimeType: file.type || "text/csv",
          columns,
          rows,
        });
      },
      error: (e) => reject(e),
    });
  });
}

async function parseXlsx(file: File): Promise<ParsedFile> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error("No sheets found in workbook");
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: null });
  const columns = rows[0] ? Object.keys(rows[0]) : [];
  return {
    kind: "table",
    fileName: file.name,
    mimeType: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    columns,
    rows,
  };
}

async function parseTxt(file: File): Promise<ParsedFile> {
  const text = await file.text();
  return { kind: "text", fileName: file.name, mimeType: file.type || "text/plain", text };
}

async function parsePdf(file: File): Promise<ParsedFile> {
  // Lazy import + worker config to keep bundle small.
  const pdfjs: any = await import("pdfjs-dist");
  // @ts-ignore
  const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const parts: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    parts.push(content.items.map((it: any) => it.str).join(" "));
  }
  return {
    kind: "text",
    fileName: file.name,
    mimeType: file.type || "application/pdf",
    text: parts.join("\n\n"),
  };
}

export async function parseUserFile(file: File): Promise<ParsedFile> {
  const ext = extOf(file.name);
  if (ext === "csv") return parseCsv(file);
  if (ext === "xlsx" || ext === "xls") return parseXlsx(file);
  if (ext === "txt") return parseTxt(file);
  if (ext === "pdf") return parsePdf(file);
  throw new Error(UNSUPPORTED_MESSAGE);
}

// Helpers ---------------------------------------------------------

export function toCsv(rows: Record<string, any>[], columns?: string[]): string {
  if (!rows.length) return "";
  const cols = columns ?? Object.keys(rows[0]);
  return Papa.unparse({ fields: cols, data: rows.map((r) => cols.map((c) => r[c] ?? "")) });
}

export function downloadCsv(filename: string, rows: Record<string, any>[], columns?: string[]) {
  const csv = toCsv(rows, columns);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
}

export function downloadXlsx(filename: string, rows: Record<string, any>[]) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([out], { type: "application/octet-stream" });
  triggerDownload(blob, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function summarizeForAI(parsed: ParsedFile, maxRows = 200): string {
  if (parsed.kind === "text") return (parsed.text ?? "").slice(0, 60000);
  const cols = parsed.columns ?? [];
  const rows = (parsed.rows ?? []).slice(0, maxRows);
  return JSON.stringify({ columns: cols, sample_rows: rows, total_rows: parsed.rows?.length ?? 0 });
}
